import { IPublicSession, repushVoipMasters, userSession, userSessionSchema } from 'state/session';
import { user } from 'utils/auth/user';
import { getLastSeenVersion, seedRemoteVersion } from './remoteVersion';
import { runApplyingRemote } from './remoteApplyGuard';
import { mergeVoipMasters, IVoipMastersMergeResult, TVoipMasterSlot } from './voipClaim';
import logger from 'utils/log';

/**
 * The single entry point for applying session state that came from the server — WebSocket
 * broadcasts and the reconnect read-repair GET funnel through here (AVS-4330). Keeping one
 * chokepoint means the staleness gate, the schema strip, and dirty-key protection cannot be
 * bypassed by a new call site. (The dev-only cross-tab mirror in `localSync.ts` applies state
 * on its own path with its own guard and does NOT route through here.)
 */

export interface IApplyRemoteOptions {
  /**
   * Keys with unflushed local edits. A remote payload never overwrites these — otherwise a
   * reconnect read-repair (which legitimately carries a newer version) would silently discard
   * a pending local change that has not reached the server yet.
   */
  dirtyKeys?: Iterable<keyof IPublicSession>;
}

const sameVoipMasters = (a: Record<string, TVoipMasterSlot>, b: Record<string, TVoipMasterSlot>): boolean => {
  const aKeys = Object.keys(a);
  return (
    aKeys.length === Object.keys(b).length &&
    aKeys.every(
      (key) => b[key] !== undefined && a[key].tabId === b[key].tabId && (a[key].gen ?? 0) === (b[key].gen ?? 0)
    )
  );
};

/**
 * @returns true when the payload was applied, false when it was dropped or was a no-op.
 * (A dropped payload may still contribute its `voipMasters` slice — see below.)
 */
export function applyRemoteSession(raw: unknown, options: IApplyRemoteOptions = {}): boolean {
  if (!raw || typeof raw !== 'object') {
    return false;
  }

  const rawRecord = raw as Record<string, unknown>;
  const incomingVersion = typeof rawRecord.version === 'number' ? rawRecord.version : undefined;
  const lastSeenVersion = getLastSeenVersion();

  // voipMasters is self-reconciling (per-slot gen ordering — voipClaim.ts), so it is merged
  // gen-aware OUTSIDE the version gate and the dirty-skip: either gate arbitrates with weaker
  // information than the key's own ordering and could drop or revert a takeover broadcast.
  let voipMerge: IVoipMastersMergeResult | null = null;
  if (rawRecord.voipMasters !== undefined) {
    const slice = userSessionSchema.shape.voipMasters.safeParse(rawRecord.voipMasters);
    if (slice.success) {
      voipMerge = mergeVoipMasters(userSession().get('voipMasters'), slice.data, user.userPseudonym || undefined);
    }
  }

  // Applies the merged map when it changes anything, and repairs the server when our own
  // newer claim won — the last-writer-wins server may still hold the losing map.
  const applyVoipMergeAndRepair = () => {
    if (!voipMerge) return;
    const merged = voipMerge.merged;
    if (!sameVoipMasters(userSession().get('voipMasters') ?? {}, merged)) {
      runApplyingRemote(() => userSession().setValues({ voipMasters: merged }));
    }
    if (voipMerge.ownSlotDefended) {
      repushVoipMasters();
    }
  };

  // Drop anything strictly older than what we already have. Deliberately `<` and not `<=`:
  // an emitOnly broadcast echoes the sender's CURRENT version without advancing it, so `<=`
  // would drop every legitimate emitOnly update. A payload with no version at all (an older
  // server, or session-end) cannot be ordered, so it is applied — preserving prior behaviour.
  if (incomingVersion !== undefined && lastSeenVersion !== undefined && incomingVersion < lastSeenVersion) {
    logger.info('[Session] Dropping stale remote payload', { incomingVersion, lastSeenVersion });
    applyVoipMergeAndRepair();
    return false;
  }

  const parsed = userSessionSchema.partial().strip().safeParse(rawRecord);
  if (!parsed.success) {
    logger.warn('[Session] Rejecting malformed remote payload', parsed.error?.message);
    applyVoipMergeAndRepair();
    return false;
  }

  // Apply ONLY keys the payload actually carried. A schema parse materialises a value for
  // every key that has a `.default()` (voipMasters -> {}, streamActive -> true, ...), so
  // applying `parsed.data` wholesale would stomp local state with defaults the server never
  // sent — reintroducing the very clobbering this gate exists to stop.
  const dirtyKeys = new Set<keyof IPublicSession>(options.dirtyKeys ?? []);
  const applied: Record<string, unknown> = {};

  Object.keys(parsed.data).forEach((key) => {
    if (!(key in rawRecord)) {
      return;
    }
    if (key === 'voipMasters' && voipMerge) {
      return; // handled by the gen-aware merge below
    }
    if (dirtyKeys.has(key as keyof IPublicSession)) {
      logger.info('[Session] Keeping unflushed local edit over remote value', { key });
      return;
    }
    applied[key] = (parsed.data as Record<string, unknown>)[key];
  });

  if (voipMerge && !sameVoipMasters(userSession().get('voipMasters') ?? {}, voipMerge.merged)) {
    applied.voipMasters = voipMerge.merged;
  }

  // Track the version even for a no-op payload: we have still observed that server state.
  seedRemoteVersion(incomingVersion);

  const hasChanges = Object.keys(applied).length > 0;
  if (hasChanges) {
    // Apply under the remote-apply guard so `syncToServer` treats this as inbound state, not a
    // local edit — otherwise it would mark these keys dirty and the next broadcast for them would
    // be self-skipped by the dirty-key gate (AVS-4330).
    runApplyingRemote(() => userSession().setValues(applied as Partial<IPublicSession>));
  }
  if (voipMerge?.ownSlotDefended) {
    repushVoipMasters();
  }
  return hasChanges;
}
